import React, { useState, useEffect } from "react";
import { format, parseISO, isBefore, parse, addHours, isWithinInterval } from "date-fns";
import { collection, query, where, getDocs, orderBy, doc, updateDoc, addDoc, getDoc } from "firebase/firestore";
import { getFirestore } from "firebase/firestore";
import { useFirebase } from "@/contexts/FirebaseContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, User, CheckCircle, XCircle, CalendarClock, Mail, Phone, MapPin, Car, Info, BookOpen, BarChart, Search, Filter, SortAsc, SortDesc } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

interface Schedule {
  id: string;
  studentName: string;
  studentEmail: string;
  studentPhone: string;
  studentAddress: string;
  studentPostcode: string;
  studentGearbox: string;
  studentId: string;
  date: string;
  startTime: string;
  endTime: string;
  duration: string;
  status: string;
  courseType: string;
  courseName: string;
  courseSubtype: string;
  courseId: string;
  enrollmentId: string;
  totalHours: number;
  hoursTaken: number;
  hoursRemaining: number;
  vehicleType: string;
  licenseType: string;
  venue: string;
}

interface UserData {
  id: string;
  address?: string;
  postcode?: string;
  gearbox?: string;
  phone?: string;
  role: string;
  [key: string]: any;
}

interface LessonDetails {
  notes?: string;
  progress?: string;
  feedback?: string;
  skills?: {
    name: string;
    rating: number;
    notes?: string;
  }[];
  topics?: string[];
  nextSteps?: string[];
}

interface Lesson {
  id: string;
  date: string;
  skills?: string[];
  topics?: string[];
  nextSteps?: string[];
  status: string;
}

interface StudentProgress {
  completedLessons: number;
  totalLessons: number;
  lastLessonDate: string;
  skills: string[];
  topics: string[];
  nextSteps: string[];
}

interface Course {
  id: string;
  title: string;
  type: string;
  subtype: string;
  totalHours: number;
  vehicleType: string;
  licenseType: string;
}

interface Enrollment {
  id: string;
  courseId: string;
  courseType: string;
  courseName: string;
  courseSubtype: string;
  totalHours: number;
  hoursTaken: number;
  hoursRemaining: number;
  studentName: string;
  studentEmail: string;
  studentId: string;
  instructorId: string;
  instructorName: string;
  instructorEmail: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

interface UnavailabilityPeriod {
  startTime: string;
  endTime: string;
}

const timeSlots = [
  "09:00", "10:00", "11:00", "12:00", "13:00", 
  "14:00", "15:00", "16:00", "17:00"
];

// Utility to safely parse ISO date strings
function safeParseISO(dateString: string): Date | null {
  if (!dateString) return null;
  try {
    const d = parseISO(dateString);
    if (isNaN(d.getTime())) return null;
    return d;
  } catch {
    return null;
  }
}

const InstructorScheduleList = () => {
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [scheduleAction, setScheduleAction] = useState<{ id: string; action: "confirm" | "decline" } | null>(null);
  const [rescheduleDialog, setRescheduleDialog] = useState<{ open: boolean; schedule: Schedule | null }>({
    open: false,
    schedule: null
  });
  const [completeLessonDialog, setCompleteLessonDialog] = useState<{ open: boolean; schedule: Schedule | null }>({
    open: false,
    schedule: null
  });
  const [lessonDetails, setLessonDetails] = useState<LessonDetails>({
    notes: '',
    progress: '',
    feedback: '',
    skills: [],
    topics: [],
    nextSteps: []
  });
  const [newDate, setNewDate] = useState<Date | null>(null);
  const [newTime, setNewTime] = useState<string>("");
  const [availableTimeSlots, setAvailableTimeSlots] = useState<string[]>([]);
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);
  const { currentUser, getUserByEmail } = useFirebase();
  const { toast } = useToast();
  const [viewDetailsDialog, setViewDetailsDialog] = useState<{ open: boolean; schedule: Schedule | null }>({
    open: false,
    schedule: null
  });
  const [studentProgress, setStudentProgress] = useState<StudentProgress | null>(null);
  const [studentDetails, setStudentDetails] = useState<any>(null);
  const [isLoadingStudentDetails, setIsLoadingStudentDetails] = useState(false);
  const [studentDetailsError, setStudentDetailsError] = useState<string | null>(null);
  const [isCompletingLesson, setIsCompletingLesson] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [sortBy, setSortBy] = useState("date");
  const [sortOrder, setSortOrder] = useState("asc");
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  useEffect(() => {
    const fetchSchedules = async () => {
      if (!currentUser?.email) {
        console.log("No instructor email found");
        return;
      }

      try {
        setLoading(true);
        
        // Get the instructor's Firestore document
        const instructorDoc = await getUserByEmail(currentUser.email);
        if (!instructorDoc) {
          console.log("No instructor document found");
          return;
        }

        const instructorId = instructorDoc.id;
        console.log("Fetching schedules for instructor:", instructorId);
        
        const db = getFirestore();
        const schedulesRef = collection(db, "schedules");
        
        const q = query(
          schedulesRef,
          where("instructorId", "==", instructorId),
          where("status", "in", ["pending", "confirmed", "completed"])
        );

        const querySnapshot = await getDocs(q);
        console.log("Query executed, documents found:", querySnapshot.size);

        if (querySnapshot.empty) {
          console.log("No schedules found for this instructor");
          setSchedules([]);
          return;
        }

        // Get all enrollments for the students in these schedules
        const enrollmentsRef = collection(db, "enrollments");
        const enrollmentsQuery = query(
          enrollmentsRef,
          where("instructorId", "==", instructorId)
        );
        const enrollmentsSnapshot = await getDocs(enrollmentsQuery);
        const enrollments = enrollmentsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Enrollment[];

        // Get all users (students) information
        const usersRef = collection(db, "users");
        const usersQuery = query(
          usersRef,
          where("role", "==", "student")
        );
        const usersSnapshot = await getDocs(usersQuery);
        const users = usersSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as UserData[];

        // Get all courses information
        const coursesRef = collection(db, "courses");
        const coursesSnapshot = await getDocs(coursesRef);
        const courses = coursesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Course[];

        const schedulesData = await Promise.all(querySnapshot.docs.map(async (doc) => {
          const data = doc.data();
          const enrollment = enrollments.find(e => e.id === data.enrollmentId);
          const student = users.find(u => u.id === data.studentId);
          const course = courses.find(c => c.id === enrollment?.courseId);

          // Check if this is a combined Individual/Blog lesson
          const isCombinedLesson = enrollment?.courseType === "individual" || 
                                 (enrollment?.courseName && 
                                  (enrollment.courseName.toLowerCase().includes("blog") || 
                                   enrollment.courseName.toLowerCase().includes("combined")));

          return {
            id: doc.id,
            ...data,
            studentAddress: student?.address || "",
            studentPostcode: student?.postcode || "",
            studentGearbox: student?.gearbox || "",
            studentPhone: student?.phone || "",
            courseName: isCombinedLesson ? "Combined Driving Lesson" : (course?.title || "Unknown Course"),
            courseType: course?.type || "Not specified",
            courseSubtype: course?.subtype || "Not specified",
            totalHours: course?.totalHours || 0,
            hoursTaken: enrollment?.hoursTaken || 0,
            hoursRemaining: (course?.totalHours || 0) - (enrollment?.hoursTaken || 0),
            vehicleType: course?.vehicleType || "Not specified",
            licenseType: course?.licenseType || "Not specified"
          };
        })) as Schedule[];

        // Sort by date
        schedulesData.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

        console.log("Final processed schedules data:", JSON.stringify(schedulesData, null, 2));
        setSchedules(schedulesData);
      } catch (error) {
        console.error("Error fetching schedules:", error);
        if (error instanceof Error) {
          console.error("Error message:", error.message);
          console.error("Error stack:", error.stack);
        }
        toast({
          title: "Error",
          description: "Failed to load schedules. Please try again.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchSchedules();
  }, [currentUser, getUserByEmail, toast]);

  // Filter and sort schedules
  const filteredAndSortedSchedules = schedules
    .filter((schedule) => {
      // Search by student name or course name
      const searchLower = search.toLowerCase();
      const matchesSearch = 
        schedule.studentName.toLowerCase().includes(searchLower) ||
        schedule.courseName.toLowerCase().includes(searchLower) ||
        schedule.studentEmail.toLowerCase().includes(searchLower);

      // Filter by status
      const matchesFilter = filter === "all" || schedule.status === filter;

      // Filter by date
      const matchesDate = !selectedDate || (safeParseISO(schedule.date) && format(safeParseISO(schedule.date)!, "yyyy-MM-dd") === format(selectedDate, "yyyy-MM-dd"));

      return matchesSearch && matchesFilter && matchesDate;
    })
    .sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case "date":
          comparison = new Date(a.date).getTime() - new Date(b.date).getTime();
          break;
        case "name":
          comparison = a.studentName.localeCompare(b.studentName);
          break;
        case "time":
          comparison = a.startTime.localeCompare(b.startTime);
          break;
        case "status":
          comparison = a.status.localeCompare(b.status);
          break;
        default:
          comparison = new Date(a.date).getTime() - new Date(b.date).getTime();
      }

      return sortOrder === "asc" ? comparison : -comparison;
    });

  // Group schedules if needed
  const groupedSchedules = filter === "groupByName" 
    ? filteredAndSortedSchedules.reduce((groups, schedule) => {
        const key = schedule.studentName;
        if (!groups[key]) {
          groups[key] = [];
        }
        groups[key].push(schedule);
        return groups;
      }, {} as { [key: string]: Schedule[] })
    : filter === "groupByDate"
    ? filteredAndSortedSchedules.reduce((groups, schedule) => {
        const key = safeParseISO(schedule.date) ? format(safeParseISO(schedule.date)!, "yyyy-MM-dd") : 'Invalid date';
        if (!groups[key]) {
          groups[key] = [];
        }
        groups[key].push(schedule);
        return groups;
      }, {} as { [key: string]: Schedule[] })
    : null;

  const handleConfirmSchedule = async () => {
    if (!scheduleAction || scheduleAction.action !== 'confirm') return;
    
    try {
      const db = getFirestore();
      const scheduleRef = doc(db, "schedules", scheduleAction.id);
      
      await updateDoc(scheduleRef, {
        status: "confirmed",
        updatedAt: new Date()
      });
      
      // Update local state
      setSchedules(prevSchedules => 
        prevSchedules.map(schedule => 
          schedule.id === scheduleAction.id 
            ? { ...schedule, status: "confirmed" as const } 
            : schedule
        )
      );
      
      toast({
        title: "Schedule Confirmed",
        description: "The lesson has been confirmed.",
      });
    } catch (error) {
      console.error("Error confirming schedule:", error);
      toast({
        title: "Action Failed",
        description: "There was a problem processing your request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setScheduleAction(null);
    }
  };

  const handleDeclineSchedule = async () => {
    if (!scheduleAction || scheduleAction.action !== 'decline') return;
    
    try {
      const db = getFirestore();
      const scheduleRef = doc(db, "schedules", scheduleAction.id);
      
      // Get the schedule data to check if it was confirmed and needs hour refund
      const scheduleDoc = await getDocs(query(collection(db, "schedules"), where("__name__", "==", scheduleAction.id)));
      const scheduleData = scheduleDoc.docs[0]?.data();
      
      // If this was a confirmed lesson, refund the hours to the student's enrollment
      if (scheduleData?.status === "confirmed" && scheduleData?.enrollmentId && scheduleData?.duration) {
        const enrollmentRef = doc(db, "enrollments", scheduleData.enrollmentId);
        const enrollmentDoc = await getDocs(query(collection(db, "enrollments"), where("__name__", "==", scheduleData.enrollmentId)));
        
        if (enrollmentDoc.docs.length > 0) {
          const enrollmentData = enrollmentDoc.docs[0].data();
          const newHoursTaken = Math.max(0, (enrollmentData.hoursTaken || 0) - Number(scheduleData.duration));
          const newHoursRemaining = (enrollmentData.totalHours || 0) - newHoursTaken;

          await updateDoc(enrollmentRef, {
            hoursTaken: newHoursTaken,
            hoursRemaining: newHoursRemaining
          });
        }
      }
      
      await updateDoc(scheduleRef, {
        status: "cancelled",
        cancelledAt: new Date().toISOString(),
        cancelledBy: currentUser?.email || currentUser?.uid,
        updatedAt: new Date()
      });
      
      // Update local state
      setSchedules(prevSchedules => 
        prevSchedules.map(schedule => 
          schedule.id === scheduleAction.id 
            ? { ...schedule, status: "cancelled" as const } 
            : schedule
        )
      );
      
      toast({
        title: "Schedule Cancelled",
        description: "The lesson has been cancelled.",
      });
    } catch (error) {
      console.error("Error cancelling schedule:", error);
      toast({
        title: "Action Failed",
        description: "There was a problem processing your request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setScheduleAction(null);
    }
  };

  const handleCancelSchedule = async (scheduleId: string) => {
    const shouldCancel = window.confirm(
      "Are you sure you want to cancel this lesson? This action cannot be undone."
    );

    if (!shouldCancel) return;

    setIsCancelling(true);
    try {
      const db = getFirestore();
      const scheduleRef = doc(db, "schedules", scheduleId);
      
      // Get the schedule data to check if it needs hour refund
      const scheduleDoc = await getDocs(query(collection(db, "schedules"), where("__name__", "==", scheduleId)));
      const scheduleData = scheduleDoc.docs[0]?.data();
      
      // If this was a confirmed lesson, refund the hours to the student's enrollment
      if (scheduleData?.status === "confirmed" && scheduleData?.enrollmentId && scheduleData?.duration) {
        const enrollmentRef = doc(db, "enrollments", scheduleData.enrollmentId);
        const enrollmentDoc = await getDocs(query(collection(db, "enrollments"), where("__name__", "==", scheduleData.enrollmentId)));
        
        if (enrollmentDoc.docs.length > 0) {
          const enrollmentData = enrollmentDoc.docs[0].data();
          const newHoursTaken = Math.max(0, (enrollmentData.hoursTaken || 0) - Number(scheduleData.duration));
          const newHoursRemaining = (enrollmentData.totalHours || 0) - newHoursTaken;

          await updateDoc(enrollmentRef, {
            hoursTaken: newHoursTaken,
            hoursRemaining: newHoursRemaining
          });
        }
      }
      
      await updateDoc(scheduleRef, {
        status: "cancelled",
        cancelledAt: new Date().toISOString(),
        cancelledBy: currentUser?.email || currentUser?.uid,
        updatedAt: new Date()
      });
      
      // Update local state
      setSchedules(prevSchedules => 
        prevSchedules.map(schedule => 
          schedule.id === scheduleId 
            ? { ...schedule, status: "cancelled" as const } 
            : schedule
        )
      );
      
      toast({
        title: "Lesson Cancelled",
        description: "The lesson has been cancelled successfully.",
      });
    } catch (error) {
      console.error("Error cancelling schedule:", error);
      toast({
        title: "Action Failed",
        description: "There was a problem cancelling the lesson. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsCancelling(false);
    }
  };

  const checkAvailability = async (date: Date) => {
    if (!date || !rescheduleDialog.schedule) return;
    
    setIsCheckingAvailability(true);
    try {
      const db = getFirestore();
      const formattedDate = format(date, "yyyy-MM-dd");
      
      // Get instructor's schedule for the selected date
      const schedulesRef = collection(db, "schedules");
      const scheduleQuery = query(
        schedulesRef,
        where("instructorId", "==", currentUser?.uid),
        where("date", "==", formattedDate),
        where("status", "in", ["confirmed", "pending"])
      );
      
      const scheduleSnapshot = await getDocs(scheduleQuery);
      const existingSchedules = scheduleSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Schedule[];

      // Get instructor's unavailability from instructor_availability collection
      const availabilityRef = doc(db, 'instructor_availability', currentUser?.email || '');
      const availabilityDoc = await getDoc(availabilityRef);
      const unavailableSlots = availabilityDoc.exists() ? availabilityDoc.data().unavailableSlots || [] : [];
      const dateUnavailableSlots = unavailableSlots.filter((slot: any) => slot.date === formattedDate);

      // Filter available time slots
      const available = timeSlots.filter(time => {
        // Check if time slot conflicts with existing schedules
        const hasScheduleConflict = existingSchedules.some(schedule => {
          if (schedule.id === rescheduleDialog.schedule?.id) return false;
          const scheduleStart = parse(schedule.startTime, "HH:mm", new Date());
          const scheduleEnd = addHours(scheduleStart, Number(schedule.duration));
          // Add buffer time to schedule conflict check - instructors need 15-minute break between lessons
          const scheduleEndWithBuffer = addHours(scheduleEnd, 15 / 60); // 15 minutes buffer
          const slotStart = parse(time, "HH:mm", new Date());
          const slotDuration = Number(rescheduleDialog.schedule?.duration || "1");
          const slotEnd = addHours(slotStart, slotDuration);
          return isWithinInterval(slotStart, { start: scheduleStart, end: scheduleEndWithBuffer }) ||
                 isWithinInterval(slotEnd, { start: scheduleStart, end: scheduleEndWithBuffer });
        });

        // Check if time slot is within unavailable periods
        const isUnavailable = dateUnavailableSlots.some(slot => {
          if (slot.isFullDay) return true;
          
          const slotStart = parse(slot.startTime, "HH:mm", new Date());
          const slotEnd = parse(slot.endTime, "HH:mm", new Date());
          const timeStart = parse(time, "HH:mm", new Date());
          const timeDuration = Number(rescheduleDialog.schedule?.duration || "1");
          const timeEnd = addHours(timeStart, timeDuration);
          return isWithinInterval(timeStart, { start: slotStart, end: slotEnd }) ||
                 isWithinInterval(timeEnd, { start: slotStart, end: slotEnd });
        });

        return !hasScheduleConflict && !isUnavailable;
      });

      setAvailableTimeSlots(available);
    } catch (error) {
      console.error("Error checking availability:", error);
      toast({
        title: "Error",
        description: "Failed to check availability. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsCheckingAvailability(false);
    }
  };

  const handleReschedule = async () => {
    if (!rescheduleDialog.schedule || !newDate || !newTime) {
      toast({
        title: "Missing Information",
        description: "Please select a new date and time for rescheduling.",
        variant: "destructive",
      });
      return;
    }

    // Validate that the new date is not in the past
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDate = new Date(newDate);
    selectedDate.setHours(0, 0, 0, 0);

    if (selectedDate < today) {
      toast({
        title: "Invalid Date",
        description: "Cannot reschedule to a past date.",
        variant: "destructive",
      });
      return;
    }

    try {
      const db = getFirestore();
      const scheduleRef = doc(db, "schedules", rescheduleDialog.schedule.id);
      
      const formattedDate = format(newDate, "yyyy-MM-dd");
      
      await updateDoc(scheduleRef, {
        date: formattedDate,
        startTime: newTime,
        updatedAt: new Date().toISOString()
      });
      
      // Update local state
      setSchedules(prevSchedules => 
        prevSchedules.map(schedule => 
          schedule.id === rescheduleDialog.schedule?.id 
            ? { 
                ...schedule, 
                date: formattedDate,
                startTime: newTime
              } 
            : schedule
        )
      );
      
      toast({
        title: "Schedule Updated",
        description: "The lesson has been rescheduled successfully.",
      });

      // Close the dialog and reset state
      setRescheduleDialog({ open: false, schedule: null });
      setNewDate(null);
      setNewTime("");
    } catch (error) {
      console.error("Error rescheduling:", error);
      toast({
        title: "Action Failed",
        description: "There was a problem rescheduling the lesson. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleCompleteLesson = async () => {
    if (!completeLessonDialog.schedule) return;

    try {
      setIsCompletingLesson(true);
      const db = getFirestore();
      const scheduleRef = doc(db, "schedules", completeLessonDialog.schedule.id);
      
      // Update schedule status
      await updateDoc(scheduleRef, {
        status: "completed",
        updatedAt: new Date()
      });

      // Create a new lesson record with all required fields
      const lessonsRef = collection(db, "lessons");
      const lessonData = {
        date: completeLessonDialog.schedule.date,
        startTime: completeLessonDialog.schedule.startTime,
        endTime: completeLessonDialog.schedule.endTime,
        duration: completeLessonDialog.schedule.duration,
        status: "completed",
        instructorName: currentUser?.displayName || "Unknown Instructor",
        instructorEmail: currentUser?.email || "",
        courseName: completeLessonDialog.schedule.courseName || "Unnamed Course",
        courseId: completeLessonDialog.schedule.courseId || "",
        enrollmentId: completeLessonDialog.schedule.enrollmentId || "",
        venue: completeLessonDialog.schedule.venue || "Not specified",
        studentId: completeLessonDialog.schedule.studentId || "",
        studentName: completeLessonDialog.schedule.studentName || "Unknown Student",
        studentEmail: completeLessonDialog.schedule.studentEmail || "",
        notes: lessonDetails.notes || "",
        progress: lessonDetails.progress || "",
        feedback: lessonDetails.feedback || "",
        topics: lessonDetails.topics || [],
        nextSteps: lessonDetails.nextSteps || [],
        createdAt: new Date()
      };

      await addDoc(lessonsRef, lessonData);
      
      // Update local state
      setSchedules(prevSchedules => 
        prevSchedules.map(schedule => 
          schedule.id === completeLessonDialog.schedule?.id 
            ? { ...schedule, status: "completed" as const } 
            : schedule
        )
      );
      
      toast({
        title: "Lesson Completed",
        description: "The lesson has been marked as completed and details have been saved.",
      });

      // Reset state
      setCompleteLessonDialog({ open: false, schedule: null });
      setLessonDetails({
        notes: '',
        progress: '',
        feedback: '',
        skills: [],
        topics: [],
        nextSteps: []
      });
    } catch (error) {
      console.error("Error completing lesson:", error);
      toast({
        title: "Action Failed",
        description: "There was a problem completing the lesson. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsCompletingLesson(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "confirmed":
        return "bg-green-100 text-green-800";
      case "completed":
        return "bg-blue-100 text-blue-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const fetchStudentProgress = async (studentId: string) => {
    try {
      const db = getFirestore();
      const lessonsRef = collection(db, 'lessons');
      const q = query(
        lessonsRef,
        where('studentId', '==', studentId),
        where('status', '==', 'completed')
      );
      
      const querySnapshot = await getDocs(q);
      const lessons = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Lesson));
      
      // Calculate progress
      const progress: StudentProgress = {
        completedLessons: lessons.length,
        totalLessons: 0, // This should come from the course requirements
        lastLessonDate: lessons.length > 0 ? lessons[lessons.length - 1].date : '',
        skills: [],
        topics: [],
        nextSteps: []
      };

      // Collect all skills, topics, and next steps
      lessons.forEach(lesson => {
        if (lesson.skills) progress.skills.push(...lesson.skills);
        if (lesson.topics) progress.topics.push(...lesson.topics);
        if (lesson.nextSteps) progress.nextSteps.push(...lesson.nextSteps);
      });

      // Remove duplicates
      progress.skills = [...new Set(progress.skills)];
      progress.topics = [...new Set(progress.topics)];
      progress.nextSteps = [...new Set(progress.nextSteps)];

      setStudentProgress(progress);
    } catch (error) {
      console.error('Error fetching student progress:', error);
    }
  };

  const handleViewDetails = async (schedule: Schedule) => {
    setViewDetailsDialog({ open: true, schedule });
    setIsLoadingStudentDetails(true);
    setStudentDetailsError(null);

    try {
      const db = getFirestore();
      
      // Fetch student details from Firestore
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('id', '==', schedule.studentId));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        throw new Error('Student not found');
      }

      const studentDoc = querySnapshot.docs[0];
      const studentData = studentDoc.data();

      // Fetch student's enrollments
      const enrollmentsRef = collection(db, 'enrollments');
      const enrollmentsQuery = query(
        enrollmentsRef,
        where('studentId', '==', schedule.studentId)
      );
      const enrollmentsSnapshot = await getDocs(enrollmentsQuery);
      
      // Fetch student's completed lessons
      const lessonsRef = collection(db, 'lessons');
      const lessonsQuery = query(
        lessonsRef,
        where('studentId', '==', schedule.studentId),
        where('status', '==', 'completed')
      );
      const lessonsSnapshot = await getDocs(lessonsQuery);
      const completedLessons = lessonsSnapshot.size;

      // Create student details object with all available information
      setStudentDetails({
        id: studentDoc.id,
        firstName: studentData.firstName || '',
        lastName: studentData.lastName || '',
        email: studentData.email || '',
        phone: studentData.phone || 'Not provided',
        address: studentData.address || 'Not provided',
        postcode: studentData.postcode || 'Not provided',
        gearbox: studentData.gearbox || 'Not specified',
        licenseNumber: studentData.licenseNumber || 'Not provided',
        progress: {
          completedLessons: completedLessons,
          totalLessons: enrollmentsSnapshot.size,
          lastLessonDate: studentData.lastLessonDate || 'No lessons completed'
        }
      });

      // Fetch student progress
      if (schedule.studentId) {
        await fetchStudentProgress(schedule.studentId);
      }
    } catch (error) {
      console.error('Error fetching student details:', error);
      setStudentDetailsError(error instanceof Error ? error.message : 'Failed to load student details');
    } finally {
      setIsLoadingStudentDetails(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout role="instructor">
        <div className="flex justify-center items-center h-64">
          <p>Loading schedules...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role="instructor">
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Student Schedules</h1>
          <p className="text-gray-500 mt-2">View and manage your students' driving lessons</p>
        </div>

        {/* Search and Filter Section */}
        <div className="bg-white p-4 rounded-lg border shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by name or course..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Date Filter */}
            <div>
              <DatePicker
                selected={selectedDate}
                onChange={(date: Date | null) => setSelectedDate(date)}
                placeholderText="Filter by date..."
                className="w-full p-2 border rounded-md"
                dateFormat="yyyy-MM-dd"
                isClearable
              />
            </div>

            {/* Status Filter */}
            <div>
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="groupByName">Group by Name</SelectItem>
                  <SelectItem value="groupByDate">Group by Date</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Sort */}
            <div className="flex gap-2">
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="date">Date</SelectItem>
                  <SelectItem value="name">Name</SelectItem>
                  <SelectItem value="time">Time</SelectItem>
                  <SelectItem value="status">Status</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
              >
                {sortOrder === "asc" ? <SortAsc className="h-4 w-4" /> : <SortDesc className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          {/* Results Summary */}
          <div className="mt-4 pt-4 border-t text-sm text-gray-600">
            Showing {filteredAndSortedSchedules.length} of {schedules.length} schedules
            {search && ` matching "${search}"`}
            {selectedDate && ` on ${format(selectedDate, "MMMM d, yyyy")}`}
            {filter !== "all" && filter !== "groupByName" && filter !== "groupByDate" && ` with status "${filter}"`}
          </div>
        </div>

        {schedules.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Calendar className="h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-xl font-medium mb-2">No Scheduled Lessons</h3>
              <p className="text-gray-500 text-center mb-6 max-w-md">
                You don't have any scheduled lessons yet.
              </p>
            </CardContent>
          </Card>
        ) : filteredAndSortedSchedules.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Search className="h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-xl font-medium mb-2">No Results Found</h3>
              <p className="text-gray-500 text-center mb-6 max-w-md">
                No schedules match your current search and filter criteria.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {groupedSchedules ? (
              // Render grouped schedules
              Object.entries(groupedSchedules).map(([groupKey, groupSchedules]) => (
                <div key={groupKey} className="space-y-4">
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <h3 className="font-semibold text-lg">
                      {filter === "groupByName" ? groupKey : (safeParseISO(groupKey) ? format(safeParseISO(groupKey)!, "EEEE, MMMM d, yyyy") : 'Invalid date')}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {groupSchedules.length} lesson{groupSchedules.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                  {groupSchedules.map((schedule) => (
                    <Card key={schedule.id}>
                      <CardContent className="p-6">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h3 className="text-lg font-semibold">{schedule.studentName}</h3>
                            <p className="text-sm text-gray-500">{schedule.courseName}</p>
                          </div>
                          <Badge
                            className={
                              schedule.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                              schedule.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              schedule.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                              schedule.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }
                          >
                            {schedule.status.charAt(0).toUpperCase() + schedule.status.slice(1)}
                          </Badge>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-gray-500" />
                            <span>{safeParseISO(schedule.date) ? format(safeParseISO(schedule.date)!, 'MMMM d, yyyy') : 'Invalid date'}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-gray-500" />
                            <span>{schedule.startTime} - {schedule.endTime} ({schedule.duration} hour(s))</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-gray-500" />
                            <span>{schedule.venue || 'Venue not specified'}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Car className="h-4 w-4 text-gray-500" />
                            <span>{schedule.studentGearbox || 'Gearbox not specified'}</span>
                          </div>
                        </div>

                        {/* Student Contact Information */}
                        <div className="bg-gray-50 p-3 rounded-lg mb-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div className="flex items-center gap-2">
                              <Mail className="h-4 w-4 text-gray-500" />
                              <span className="text-sm">{schedule.studentEmail}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Phone className="h-4 w-4 text-gray-500" />
                              <span className="text-sm">{schedule.studentPhone || 'Phone not provided'}</span>
                            </div>
                            <div className="flex items-center gap-2 col-span-2">
                              <MapPin className="h-4 w-4 text-gray-500" />
                              <span className="text-sm">
                                {schedule.studentAddress ? 
                                  `${schedule.studentAddress}${schedule.studentPostcode ? `, ${schedule.studentPostcode}` : ''}` : 
                                  'Address not provided'}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            className="flex-1"
                            onClick={() => handleViewDetails(schedule)}
                          >
                            <Info className="h-4 w-4 mr-2" />
                            View Details
                          </Button>
                          {schedule.status === "pending" && (
                            <>
                              <Button
                                variant="outline"
                                className="flex-1 bg-green-50 text-green-700 hover:bg-green-100"
                                onClick={() => setScheduleAction({ id: schedule.id, action: "confirm" })}
                              >
                                <CheckCircle className="h-4 w-4 mr-2" />
                                Confirm
                              </Button>
                              <Button
                                variant="outline"
                                className="flex-1 bg-red-50 text-red-700 hover:bg-red-100"
                                onClick={() => setScheduleAction({ id: schedule.id, action: "decline" })}
                              >
                                <XCircle className="h-4 w-4 mr-2" />
                                Decline
                              </Button>
                            </>
                          )}
                          {schedule.status === "confirmed" && (
                            <>
                              <Button
                                variant="outline"
                                className="flex-1"
                                onClick={() => setRescheduleDialog({ open: true, schedule })}
                              >
                                <CalendarClock className="h-4 w-4 mr-2" />
                                Reschedule
                              </Button>
                              <Button
                                variant="outline"
                                className="flex-1 bg-red-50 text-red-700 hover:bg-red-100"
                                onClick={() => handleCancelSchedule(schedule.id)}
                                disabled={isCancelling}
                              >
                                <XCircle className="h-4 w-4 mr-2" />
                                {isCancelling ? 'Cancelling...' : 'Cancel'}
                              </Button>
                              <Button
                                variant="outline"
                                className="flex-1 bg-blue-50 text-blue-700 hover:bg-blue-100"
                                onClick={() => setCompleteLessonDialog({ open: true, schedule })}
                              >
                                <CheckCircle className="h-4 w-4 mr-2" />
                                Complete Lesson
                              </Button>
                            </>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ))
            ) : (
              // Render regular filtered schedules
              filteredAndSortedSchedules.map((schedule) => (
                <Card key={schedule.id}>
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-lg font-semibold">{schedule.studentName}</h3>
                        <p className="text-sm text-gray-500">{schedule.courseName}</p>
                      </div>
                      <Badge
                        className={
                          schedule.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                          schedule.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          schedule.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                          schedule.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }
                      >
                        {schedule.status.charAt(0).toUpperCase() + schedule.status.slice(1)}
                      </Badge>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-gray-500" />
                        <span>{safeParseISO(schedule.date) ? format(safeParseISO(schedule.date)!, 'MMMM d, yyyy') : 'Invalid date'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-gray-500" />
                        <span>{schedule.startTime} - {schedule.endTime} ({schedule.duration} hour(s))</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-gray-500" />
                        <span>{schedule.venue || 'Venue not specified'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Car className="h-4 w-4 text-gray-500" />
                        <span>{schedule.studentGearbox || 'Gearbox not specified'}</span>
                      </div>
                    </div>

                    {/* Student Contact Information */}
                    <div className="bg-gray-50 p-3 rounded-lg mb-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-gray-500" />
                          <span className="text-sm">{schedule.studentEmail}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-gray-500" />
                          <span className="text-sm">{schedule.studentPhone || 'Phone not provided'}</span>
                        </div>
                        <div className="flex items-center gap-2 col-span-2">
                          <MapPin className="h-4 w-4 text-gray-500" />
                          <span className="text-sm">
                            {schedule.studentAddress ? 
                              `${schedule.studentAddress}${schedule.studentPostcode ? `, ${schedule.studentPostcode}` : ''}` : 
                              'Address not provided'}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        className="flex-1"
                        onClick={() => handleViewDetails(schedule)}
                      >
                        <Info className="h-4 w-4 mr-2" />
                        View Details
                      </Button>
                      {schedule.status === "pending" && (
                        <>
                          <Button
                            variant="outline"
                            className="flex-1 bg-green-50 text-green-700 hover:bg-green-100"
                            onClick={() => setScheduleAction({ id: schedule.id, action: "confirm" })}
                          >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Confirm
                          </Button>
                          <Button
                            variant="outline"
                            className="flex-1 bg-red-50 text-red-700 hover:bg-red-100"
                            onClick={() => setScheduleAction({ id: schedule.id, action: "decline" })}
                          >
                            <XCircle className="h-4 w-4 mr-2" />
                            Decline
                          </Button>
                        </>
                      )}
                      {schedule.status === "confirmed" && (
                        <>
                          <Button
                            variant="outline"
                            className="flex-1"
                            onClick={() => setRescheduleDialog({ open: true, schedule })}
                          >
                            <CalendarClock className="h-4 w-4 mr-2" />
                            Reschedule
                          </Button>
                          <Button
                            variant="outline"
                            className="flex-1 bg-red-50 text-red-700 hover:bg-red-100"
                            onClick={() => handleCancelSchedule(schedule.id)}
                            disabled={isCancelling}
                          >
                            <XCircle className="h-4 w-4 mr-2" />
                            {isCancelling ? 'Cancelling...' : 'Cancel'}
                          </Button>
                          <Button
                            variant="outline"
                            className="flex-1 bg-blue-50 text-blue-700 hover:bg-blue-100"
                            onClick={() => setCompleteLessonDialog({ open: true, schedule })}
                          >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Complete Lesson
                          </Button>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}
      </div>

      {/* Reschedule Dialog */}
      <Dialog open={rescheduleDialog.open} onOpenChange={(open) => setRescheduleDialog({ open, schedule: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reschedule Lesson</DialogTitle>
            <DialogDescription>
              Select a new date and time for the lesson with {rescheduleDialog.schedule?.studentName}. The duration will remain {rescheduleDialog.schedule?.duration} hour(s).
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Current Schedule</Label>
              <div className="p-3 bg-gray-50 rounded-md">
                <p className="text-sm">
                  <span className="font-medium">Date:</span> {rescheduleDialog.schedule?.date}
                </p>
                <p className="text-sm">
                  <span className="font-medium">Time:</span> {rescheduleDialog.schedule?.startTime} - {rescheduleDialog.schedule?.endTime}
                </p>
                <p className="text-sm">
                  <span className="font-medium">Duration:</span> {rescheduleDialog.schedule?.duration} hour(s)
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label>New Date</Label>
              <DatePicker
                selected={newDate}
                onChange={(date: Date | null) => {
                  setNewDate(date);
                  if (date) {
                    checkAvailability(date);
                  }
                }}
                minDate={new Date()}
                maxDate={new Date(new Date().setMonth(new Date().getMonth() + 2))}
                className="w-full p-2 border rounded-md"
                dateFormat="MMMM d, yyyy"
                placeholderText="Select a date"
                excludeDates={[new Date()]} // Exclude today
                filterDate={(date) => {
                  // Exclude weekends
                  const day = date.getDay();
                  return day !== 0 && day !== 6;
                }}
              />
              <p className="text-sm text-gray-500 mt-1">
                Select a date between tomorrow and 2 months from now (excluding weekends)
              </p>
            </div>

            <div className="space-y-2">
              <Label>New Time</Label>
              <div className="relative">
                <select
                  value={newTime}
                  onChange={(e) => setNewTime(e.target.value)}
                  className="w-full p-2 border rounded-md"
                  disabled={isCheckingAvailability || availableTimeSlots.length === 0}
                >
                  <option value="">Select a time</option>
                  {availableTimeSlots.map((time) => (
                    <option key={time} value={time}>
                      {time}
                    </option>
                  ))}
                </select>
                {isCheckingAvailability && (
                  <div className="absolute inset-0 bg-white/50 flex items-center justify-center">
                    <span className="text-sm text-gray-500">Checking availability...</span>
                  </div>
                )}
              </div>
              {newDate && availableTimeSlots.length === 0 && !isCheckingAvailability && (
                <p className="text-sm text-red-500 mt-1">
                  No available time slots for this date. Please select another date.
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRescheduleDialog({ open: false, schedule: null })}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleReschedule}
              disabled={!newDate || !newTime || isCheckingAvailability}
            >
              Confirm Reschedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Complete Lesson Dialog */}
      <Dialog open={completeLessonDialog.open} onOpenChange={(open) => setCompleteLessonDialog({ open, schedule: null })}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Complete Lesson</DialogTitle>
            <DialogDescription>
              Add details about the completed lesson with {completeLessonDialog.schedule?.studentName}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Notes</Label>
              <textarea
                value={lessonDetails.notes}
                onChange={(e) => setLessonDetails(prev => ({ ...prev, notes: e.target.value }))}
                className="w-full p-2 border rounded-md min-h-[100px] resize-y"
                placeholder="Add any general notes about the lesson"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Progress</Label>
              <textarea
                value={lessonDetails.progress}
                onChange={(e) => setLessonDetails(prev => ({ ...prev, progress: e.target.value }))}
                className="w-full p-2 border rounded-md min-h-[80px] resize-y"
                placeholder="Describe the student's progress"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Feedback</Label>
              <textarea
                value={lessonDetails.feedback}
                onChange={(e) => setLessonDetails(prev => ({ ...prev, feedback: e.target.value }))}
                className="w-full p-2 border rounded-md min-h-[80px] resize-y"
                placeholder="Provide feedback for the student"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Topics Covered</Label>
              <textarea
                value={lessonDetails.topics?.join('\n')}
                onChange={(e) => setLessonDetails(prev => ({ 
                  ...prev, 
                  topics: e.target.value.split('\n').filter(topic => topic.trim())
                }))}
                className="w-full p-2 border rounded-md min-h-[80px] resize-y"
                placeholder="Enter topics covered (one per line)"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Next Steps</Label>
              <textarea
                value={lessonDetails.nextSteps?.join('\n')}
                onChange={(e) => setLessonDetails(prev => ({ 
                  ...prev, 
                  nextSteps: e.target.value.split('\n').filter(step => step.trim())
                }))}
                className="w-full p-2 border rounded-md min-h-[80px] resize-y"
                placeholder="Enter next steps (one per line)"
              />
            </div>
          </div>

          <DialogFooter className="sticky bottom-0 bg-background pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => setCompleteLessonDialog({ open: false, schedule: null })}
              disabled={isCompletingLesson}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleCompleteLesson}
              disabled={isCompletingLesson}
            >
              {isCompletingLesson ? (
                <>
                  <span className="animate-spin mr-2">⏳</span>
                  Completing Lesson...
                </>
              ) : (
                'Complete Lesson'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm/Decline Alert Dialog */}
      <AlertDialog open={!!scheduleAction} onOpenChange={() => setScheduleAction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {scheduleAction?.action === "confirm" ? "Confirm Lesson" : "Decline Lesson"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {scheduleAction?.action === "confirm"
                ? "Are you sure you want to confirm this lesson?"
                : "Are you sure you want to decline this lesson?"}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={scheduleAction?.action === "confirm" ? handleConfirmSchedule : handleDeclineSchedule}
              className={scheduleAction?.action === "confirm" ? "bg-green-600" : "bg-red-600"}
            >
              {scheduleAction?.action === "confirm" ? "Confirm" : "Decline"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* View Details Dialog */}
      <Dialog open={viewDetailsDialog.open} onOpenChange={(open) => {
  setViewDetailsDialog({ open, schedule: null });
  if (!open) setStudentProgress(null);
}}>
  <DialogContent className="max-w-[95vw] md:max-w-2xl max-h-[90vh] overflow-y-auto p-4 md:p-6">
    <DialogHeader>
      <DialogTitle className="text-xl md:text-2xl">Student Schedule Details</DialogTitle>
      <DialogDescription className="text-sm md:text-base">
        View detailed information about the student and their lesson schedule.
      </DialogDescription>
    </DialogHeader>
    
    {viewDetailsDialog.schedule && (
      <div className="space-y-4 md:space-y-6">
        {/* Student Information Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <h3 className="font-semibold text-base md:text-lg flex items-center gap-2">
              <User className="h-4 w-4 md:h-5 md:w-5 text-drivewise-600" />
              Student Information
            </h3>
            <div className="space-y-2 bg-gray-50 p-3 rounded-lg">
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                <span className="text-gray-500 text-sm min-w-[80px]">Name:</span>
                <span className="font-medium text-sm md:text-base">{studentDetails?.firstName} {studentDetails?.lastName}</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                <span className="text-gray-500 text-sm min-w-[80px]">Email:</span>
                <span className="font-medium text-sm md:text-base break-all">{studentDetails?.email}</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                <span className="text-gray-500 text-sm min-w-[80px]">Phone:</span>
                <span className="font-medium text-sm md:text-base">{studentDetails?.phone}</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                <span className="text-gray-500 text-sm min-w-[80px]">Address:</span>
                <span className="font-medium text-sm md:text-base break-words">
                  {studentDetails?.address}
                  {studentDetails?.postcode && `, ${studentDetails.postcode}`}
                </span>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                <span className="text-gray-500 text-sm min-w-[80px]">Gearbox:</span>
                <span className="font-medium text-sm md:text-base">{studentDetails?.gearbox || 'Not specified'}</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                <span className="text-gray-500 text-sm min-w-[80px]">License:</span>
                <span className="font-medium text-sm md:text-base">{studentDetails?.licenseNumber || 'Not provided'}</span>
              </div>
            </div>
          </div>
          
          <div className="space-y-2">
            <h3 className="font-semibold text-base md:text-lg flex items-center gap-2">
              <Calendar className="h-4 w-4 md:h-5 md:w-5 text-drivewise-600" />
              Schedule Information
            </h3>
            <div className="space-y-2 bg-gray-50 p-3 rounded-lg">
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                <span className="text-gray-500 text-sm min-w-[80px]">Date:</span>
                <span className="font-medium text-sm md:text-base">{safeParseISO(viewDetailsDialog.schedule.date) ? format(safeParseISO(viewDetailsDialog.schedule.date)!, 'EEEE, MMMM d, yyyy') : 'Invalid date'}</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                <span className="text-gray-500 text-sm min-w-[80px]">Time:</span>
                <span className="font-medium text-sm md:text-base">{viewDetailsDialog.schedule.startTime} - {viewDetailsDialog.schedule.endTime}</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                <span className="text-gray-500 text-sm min-w-[80px]">Duration:</span>
                <span className="font-medium text-sm md:text-base">{viewDetailsDialog.schedule.duration} hour(s)</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                <span className="text-gray-500 text-sm min-w-[80px]">Venue:</span>
                <span className="font-medium text-sm md:text-base">{viewDetailsDialog.schedule.venue || 'Not specified'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Course Information Section */}
        <div className="space-y-2">
          <h3 className="font-semibold text-base md:text-lg flex items-center gap-2">
            <BookOpen className="h-4 w-4 md:h-5 md:w-5 text-drivewise-600" />
            Course Information
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="text-xs md:text-sm text-gray-500">Course Name</div>
              <div className="text-sm md:text-base font-semibold mt-1 break-words">{viewDetailsDialog.schedule?.courseName || 'Unknown Course'}</div>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="text-xs md:text-sm text-gray-500">Course Type</div>
              <div className="text-sm md:text-base font-semibold mt-1 capitalize">{viewDetailsDialog.schedule?.courseType || 'Not specified'}</div>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="text-xs md:text-sm text-gray-500">Course Subtype</div>
              <div className="text-sm md:text-base font-semibold mt-1 capitalize">{viewDetailsDialog.schedule?.courseSubtype || 'Not specified'}</div>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="text-xs md:text-sm text-gray-500">Vehicle Type</div>
              <div className="text-sm md:text-base font-semibold mt-1">{viewDetailsDialog.schedule?.vehicleType || 'Not specified'}</div>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="text-xs md:text-sm text-gray-500">License Type</div>
              <div className="text-sm md:text-base font-semibold mt-1">{viewDetailsDialog.schedule?.licenseType || 'Not specified'}</div>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="text-xs md:text-sm text-gray-500">Status</div>
              <Badge 
                className={
                  viewDetailsDialog.schedule?.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                  viewDetailsDialog.schedule?.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                  viewDetailsDialog.schedule?.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                  viewDetailsDialog.schedule?.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                  'bg-gray-100 text-gray-800'
                }
              >
                {viewDetailsDialog.schedule?.status?.charAt(0).toUpperCase() + viewDetailsDialog.schedule?.status?.slice(1)}
              </Badge>
            </div>
          </div>
        </div>

        {/* Course Progress Section */}
        <div className="space-y-2">
          <h3 className="font-semibold text-base md:text-lg flex items-center gap-2">
            <BarChart className="h-4 w-4 md:h-5 md:w-5 text-drivewise-600" />
            Course Progress
          </h3>
          <div className="bg-gray-50 p-3 rounded-lg">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <div className="text-xs md:text-sm text-gray-500">Hours Taken</div>
                <div className="text-sm md:text-base font-semibold mt-1">{viewDetailsDialog.schedule?.hoursTaken || 0} hours</div>
              </div>
              <div>
                <div className="text-xs md:text-sm text-gray-500">Total Hours</div>
                <div className="text-sm md:text-base font-semibold mt-1">{viewDetailsDialog.schedule?.totalHours || 0} hours</div>
              </div>
              <div className="col-span-1 sm:col-span-2">
                <div className="text-xs md:text-sm text-gray-500 mb-2">Progress</div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div 
                    className="bg-drivewise-600 h-2.5 rounded-full" 
                    style={{ 
                      width: `${((viewDetailsDialog.schedule?.hoursTaken || 0) / (viewDetailsDialog.schedule?.totalHours || 1)) * 100}%` 
                    }}
                  />
                </div>
                <div className="text-xs md:text-sm text-gray-600 mt-1">
                  {viewDetailsDialog.schedule?.hoursTaken || 0} of {viewDetailsDialog.schedule?.totalHours || 0} hours completed
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Student Progress Section */}
        {studentProgress && (
          <div className="space-y-2">
            <h3 className="font-semibold text-base md:text-lg flex items-center gap-2">
              <BarChart className="h-4 w-4 md:h-5 md:w-5 text-drivewise-600" />
              Student Progress
            </h3>
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <div className="text-xs md:text-sm text-gray-500">Completed Lessons</div>
                  <div className="text-sm md:text-base font-semibold mt-1">{studentProgress.completedLessons}</div>
                </div>
                <div>
                  <div className="text-xs md:text-sm text-gray-500">Total Lessons</div>
                  <div className="text-sm md:text-base font-semibold mt-1">{studentProgress.totalLessons}</div>
                </div>
                <div className="col-span-1 sm:col-span-2">
                  <div className="text-xs md:text-sm text-gray-500">Last Lesson</div>
                  <div className="text-sm md:text-base font-semibold mt-1">{studentProgress.lastLessonDate}</div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    )}
  </DialogContent>
</Dialog>
    </DashboardLayout>
  );
};

export default InstructorScheduleList;
